/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import React from 'react';
import { ICON_CHECK_CIRCLE, ICON_CONTENT_COPY, ICON_INFO, ICON_REPORT } from '../../Icons';
import { ButtonList } from '../ButtonList';
import { SdsIcon } from '../SdsIcon';
import { Tooltip, TOOLTIP_POSITION_TOP, TooltipProps } from '../Tooltip';
import { CardProps } from './Card.props';
import { CARD_FORMAT_BUTTON } from './types/CardFormat.types';
import { CARD_TYPE_DANGER, CARD_TYPE_INFO, CARD_TYPE_SUCCESS } from './types/CardType.types';

/**
 * SDS Card component.
 */
export const Card = (props: CardProps): JSX.Element => {
  const {
    buttonListProps,
    cardLabel,
    classes,
    content,
    customClick,
    format,
    icon,
    subtitle,
    title,
    type,
    showCopyIcon,
    ...rest
  } = {
    ...props,
  };

  const cardTypeClass = `sds-card-icon sds-card-type--${type}`;
  const cardContainerClass = clsx(`sds-card-container`, classes, {
    [`card--button`]: format === CARD_FORMAT_BUTTON,
    [`card--standard`]: format !== CARD_FORMAT_BUTTON,
  });

  const handleCopy = async (): Promise<void> => {
    if (content) {
      try {
        await navigator.clipboard.writeText(content);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to copy: ', err);
      }
    }
  };

  const getIcon = (): JSX.Element => {
    if (icon) {
      return icon;
    }

    switch (type) {
      case CARD_TYPE_DANGER:
        return ICON_REPORT;
      case CARD_TYPE_INFO:
        return ICON_INFO;
      case CARD_TYPE_SUCCESS:
        return ICON_CHECK_CIRCLE;
      default:
        return <></>;
    }
  };

  const tooltipTopProps: TooltipProps = {
    label: 'Copy',
    position: TOOLTIP_POSITION_TOP,
  };

  const cardContent = (
    <div className="sds-card">
      {type && (
        <div className="sds-card-header">
          <div className={cardTypeClass}>
            <SdsIcon svg={getIcon()} />
          </div>
        </div>
      )}
      <div className="sds-card-body">
        <span className="sds-card-title">{title}</span>
        <span className="sds-card-subtitle">{subtitle}</span>
        {showCopyIcon ? (
          <div className="sds-card__copy-icon-and-text">
            <span className="sds-card-content">{content}</span>

            <div className="sds-card__copy-icon">
              <button aria-label="Copy to clipboard" onClick={handleCopy} type="button">
                <Tooltip {...tooltipTopProps}>
                  <SdsIcon svg={ICON_CONTENT_COPY} />
                </Tooltip>
              </button>
            </div>
          </div>
        ) : (
          <span className="sds-card-content">{content}</span>
        )}
      </div>
      {buttonListProps && (
        <div className="sds-card-buttons">
          <ButtonList {...buttonListProps} />
        </div>
      )}
    </div>
  );

  if (format === CARD_FORMAT_BUTTON) {
    return (
      <div className={cardContainerClass} {...rest}>
        <button
          aria-label={cardLabel}
          className="sds-card-link"
          onClick={(event): void => {
            event.preventDefault();
            customClick && customClick(event);
          }}
        >
          {cardContent}
        </button>
      </div>
    );
  }

  return (
    <div className={cardContainerClass} {...rest}>
      {cardContent}
    </div>
  );
};
